import { faArrowDown, faArrowLeftLong, faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import { Message } from '@twilio/conversations';
import i18next from 'i18next';
import { MutableRefObject, ReactNode, RefObject, useCallback, useEffect, useState } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import {
  Alert,
  Body,
  Button,
  DRAWER_TITLE_ID,
  DrawerBody,
  DrawerFooter,
  Icon,
  IconButton,
  Tag,
  TextInput,
  cn,
} from '@growtherapy/sprout-ui';

import { ParticipantStatus } from '../../types';
import { DrawerHeaderWrapper as DrawerHeader } from '../DrawerHeaderWrapper';
import { ChatBubble } from './ChatBubble';

export interface ChatDrawerWrapperProps {
  identity?: string;
  chatName?: string;
  messages: Message[];
  onSend: (message: string) => void;
  quickReplyComponent?: ReactNode;
  connectedAt?: number;
  footerText?: string;
  headerText?: string;
  warningBannerText?: string;
  emptyState?: ReactNode;
  isEmptyState?: boolean;
  chatStatus?: ParticipantStatus;
  onGoToChatList?: () => void;
  sendIsDisabled?: boolean;
  isError?: boolean;
  /**
   * The date shown above the conversation. `ChatDrawerWrapper` defaults it to today.
   *
   * Injected rather than read from the clock inside the view so Storybook and Chromatic
   * render the same string every day, in the same spirit as `InSessionTimer`'s
   * `getCurrentTimestamp`.
   */
  currentDate?: Date;
}

export interface ChatDrawerProps extends ChatDrawerWrapperProps {
  /** Required here: the pure view never reads the clock, the wrapper supplies it. */
  currentDate: Date;
  hasUnseenNewMessages: boolean;
  setHasUnseenNewMessages: (hasUnseenNewMessages: boolean) => void;
  scrollContainer: RefObject<HTMLDivElement>;
  isAtBottom: MutableRefObject<boolean>;
  handleScrollToBottom: () => void;
  /** Supplied by `ChatDrawerWrapper`, which reads the theme context. */
  isDarkTheme?: boolean;
}

function ChatError() {
  const t = useTranslate();
  return (
    <Alert
      className='mb-4'
      heading={t('telehealth-core:chatDrawer.unableToConnectToChat')}
      headingAs='h3'
      use='error'
    />
  );
}

export function ChatDrawer({
  identity,
  chatName,
  messages,
  onSend,
  quickReplyComponent,
  connectedAt,
  footerText,
  headerText,
  warningBannerText,
  emptyState,
  isEmptyState,
  chatStatus,
  onGoToChatList,
  sendIsDisabled,
  isError,
  isAtBottom,
  scrollContainer,
  handleScrollToBottom,
  hasUnseenNewMessages,
  setHasUnseenNewMessages,
  currentDate,
  isDarkTheme = false,
}: ChatDrawerProps) {
  const t = useTranslate();
  const [messageDraft, setMessageDraft] = useState<string>('');

  const shouldShowDate = !!connectedAt || messages.length > 0;
  const buttonIsDisabled = !messageDraft.trim() || sendIsDisabled || isError;

  /**
   * This - 20 is an arbitrary buffer we use to prevent behavior related to scroll position
   * if the user is slightly above the bottom of the scroll container.
   * It represents 20px
   **/
  const BOTTOM_SCROLL_BUFFER_PX = 20;

  const handleScroll = useCallback(
    () => {
      const container = scrollContainer.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const atBottom = scrollTop + clientHeight >= scrollHeight - BOTTOM_SCROLL_BUFFER_PX;
        isAtBottom.current = atBottom;
        if (atBottom) setHasUnseenNewMessages(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    function initializeScrollListener() {
      const container = scrollContainer.current;
      const abortController = new AbortController();
      if (container) {
        container.addEventListener('scroll', handleScroll, {
          signal: abortController.signal,
        });
        handleScrollToBottom();
      }
      return () => abortController.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleScroll],
  );

  useEffect(
    function manageScrollOnNewMessages() {
      if (isAtBottom.current) {
        handleScrollToBottom();
      } else {
        setHasUnseenNewMessages(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages],
  );

  const onSendMessage = () => {
    onSend(messageDraft);
    setMessageDraft('');
    if (isAtBottom.current) handleScrollToBottom();
  };

  const screenReaderHeaderTitle = chatName
    ? t('telehealth-core:chatDrawer.chatConversationWithName', { chatName })
    : t('telehealth-core:chatDrawer.chatConversation');
  const screenReaderHeaderStatus = chatStatus
    ? t('telehealth-core:chatDrawer.participantStatus', {
        status: chatStatus.toLowerCase(),
      })
    : '';

  return (
    <>
      <DrawerHeader>
        {onGoToChatList && (
          <button className='text-lilac-800 flex items-center rebrand:text-text-default' onClick={onGoToChatList}>
            <Icon
              icon={faArrowLeftLong}
              className={cn('mr-2 w-3', {
                'rebrand:text-text-inverse': isDarkTheme,
              })}
              aria-hidden
            />
            <Body.Xsmall
              as='span'
              className={cn('text-xsmall font-medium', {
                'rebrand:text-text-inverse': isDarkTheme,
              })}
            >
              <span className='sr-only'>{t('telehealth-core:chatDrawer.returnToListOfAllChats')}</span>
              <span aria-hidden>{t('telehealth-core:chatDrawer.allChats')}</span>
            </Body.Xsmall>
          </button>
        )}
        <span className='sr-only' id={DRAWER_TITLE_ID}>
          {screenReaderHeaderTitle + screenReaderHeaderStatus}
        </span>
        <div aria-hidden className='flex items-center w-full'>
          <div
            className={cn('fs-exclude rebrand:typography-subhead-small', {
              'rebrand:text-text-inverse': isDarkTheme,
            })}
            data-dd-privacy='mask'
            data-dd-action-name='Click on chat name'
          >
            {chatName || t('telehealth-core:controls.chat')}
          </div>
          {chatStatus && (
            <Tag className='ml-2' use={chatStatus === ParticipantStatus.IN_SESSION ? 'green' : 'neutral'}>
              {chatStatus}
            </Tag>
          )}
        </div>
      </DrawerHeader>
      {headerText && (
        <div className='px-8 py-3 rebrand:px-250 rebrand:md:px-350'>
          <Body.Xsmall
            className={cn('text-xsmall text-neutral-700 rebrand:text-text-default', {
              'rebrand:text-text-inverse': isDarkTheme,
            })}
          >
            {headerText}
          </Body.Xsmall>
        </div>
      )}
      {isEmptyState ? (
        <DrawerBody className='rebrand:py-250 rebrand:md:py-350'>{emptyState}</DrawerBody>
      ) : (
        <>
          <DrawerBody
            className={cn('bg-neutral-400 h-full overflow-y-auto flex flex-col rebrand:bg-surface-default', {
              'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
            })}
            ref={scrollContainer}
            aria-label={t('telehealth-core:chatDrawer.chatConversationWithName', { chatName })}
          >
            <Body.Xsmall
              className={cn('text-xsmall block text-neutral-700 text-center pb-6 rebrand:text-text-default', {
                'rebrand:text-text-inverse': isDarkTheme,
              })}
            >
              {connectedAt &&
                chatStatus === ParticipantStatus.IN_SESSION &&
                `Session started at ${new Date(connectedAt).toLocaleTimeString(i18next.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                })}, `}
              {shouldShowDate &&
                currentDate.toLocaleString(i18next.language, {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
            </Body.Xsmall>
            <ul aria-live='polite' aria-label={t('telehealth-core:chatDrawer.chatMessages')}>
              {messages.map((message) => {
                const isByCurrentUser = message.author === identity;
                return <ChatBubble key={message.sid} message={message} isSelf={isByCurrentUser} />;
              })}
            </ul>
            {!messages.length ? <span className='sr-only'>{t('telehealth-core:chatDrawer.noMessages')}</span> : null}
            {footerText && (
              <div className='pt-6 mt-auto'>
                <Body.Xsmall
                  className={cn('text-xsmall text-neutral-700 text-center rebrand:text-text-default', {
                    'rebrand:text-text-inverse': isDarkTheme,
                  })}
                >
                  {footerText}
                </Body.Xsmall>
              </div>
            )}
          </DrawerBody>
          {quickReplyComponent}
          {hasUnseenNewMessages && (
            <div className='border-t border-neutral-600 px-8 py-3 flex items-center justify-center rebrand:border-border-default rebrand:bg-surface-default'>
              <Button onClick={handleScrollToBottom} use='secondary'>
                {t('telehealth-core:chatDrawer.seeLatestMessage')} <Icon icon={faArrowDown} />
              </Button>
            </div>
          )}
          {warningBannerText && (
            <div
              className={cn(
                'border-t border-neutral-600 px-8 py-5 flex items-center justify-center bg-neutral-300 rebrand:border-border-default rebrand:bg-surface-default',
                {
                  'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
                },
              )}
            >
              <Body.Xsmall
                className={cn('text-xsmall text-neutral-700 leading-relaxed rebrand:text-text-default', {
                  'rebrand:text-text-inverse': isDarkTheme,
                })}
              >
                {warningBannerText}
              </Body.Xsmall>
            </div>
          )}
          <div
            className={cn('bg-neutral-200 rounded-b-xl rebrand:bg-surface-default', {
              'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
            })}
          >
            <DrawerFooter>
              <div className='flex flex-col w-full'>
                {isError && <ChatError />}
                <form className='flex w-full' onSubmit={(event) => event.preventDefault()}>
                  <TextInput
                    onChange={(event) => setMessageDraft(event.target.value)}
                    placeholder={t('telehealth-core:chatDrawer.composeYourMessage')}
                    className='mr-2 !bg-neutral-100'
                    textInputClassName={cn({
                      'rebrand:bg-surface-inverseEmphasis rebrand:text-text-inverse rebrand:placeholder:text-text-inverse rebrand:focus:ring-border-subtle arc:bg-surface-inverseEmphasis arc:focus:!bg-surface-inverseEmphasis arc:text-text-inverse arc:placeholder:text-text-inverse':
                        isDarkTheme,
                    })}
                    mask='exclude'
                    value={messageDraft}
                  />

                  <Button disabled={buttonIsDisabled} onClick={onSendMessage} type='submit' className='hidden sm:block'>
                    <span aria-hidden>{t('telehealth-core:chatDrawer.send')}</span>
                    <span className='sr-only'>
                      {chatName
                        ? t('telehealth-core:chatDrawer.sendMessageToName', {
                            chatName,
                          })
                        : t('telehealth-core:chatDrawer.sendMessage')}
                    </span>
                  </Button>
                  <IconButton
                    disabled={buttonIsDisabled}
                    onClick={onSendMessage}
                    type='submit'
                    className='sm:hidden fs-exclude'
                    data-dd-privacy='mask'
                    data-dd-action-name='Click on send message button'
                    aria-label={
                      chatName
                        ? t('telehealth-core:chatDrawer.sendMessageToName', {
                            chatName,
                          })
                        : t('telehealth-core:chatDrawer.sendMessage')
                    }
                    iconDefinition={faPaperPlaneTop}
                  />
                </form>
              </div>
            </DrawerFooter>
          </div>
        </>
      )}
    </>
  );
}
